import React from 'react'

import Dialog from '@mui/material/Dialog'
import { CircularProgress } from '@mui/material'

import { FormBuilder } from 'components/FormBuilder'
import { useForm } from 'react-hook-form'
import { useYupValidationResolver } from 'hooks/useYupValidationResolver'
import { forgotPasswordFormSchema } from './schema'
import { sendResetPasswordEmail } from 'services/user'
import { useTurnstile } from 'hooks/useTurnstile'
import { Turnstile } from '@marsidev/react-turnstile'

import IconButton from '@mui/material/IconButton'
import closeLogo from 'images/close.svg'

import { Button } from '@mui/material'

import * as styles from './ForgotPasswordModal.module.scss'

export const ForgotPasswordModal = ({
  open,
  handleClose: handleCloseProp,
  resetEmailSent: resetEmailSentProp,
  visibleCaptcha = false,
  brandLogo = '/img/logo-blue.svg',
  paperClassName,
}) => {
  const [resetEmailSent, setResetEmailSent] = React.useState(resetEmailSentProp)
  const [serverError, setServerError] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const handleClose = () => {
    handleCloseProp()
    // wrapped in timeout to prevent flicker
    setTimeout(() => setResetEmailSent(resetEmailSentProp), [200])
  }
  const { turnstileRef, getTurnstileToken } = useTurnstile()

  const recaptchaRef = React.useRef(null)

  const resolver = useYupValidationResolver(forgotPasswordFormSchema)
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      email: '',
      turnstileToken: '',
    },
    resolver,
  })

  const onSubmit = async data => {
    setLoading(true)
    const email = data.email
    const turnstileToken = await getTurnstileToken()

    try {
      const response = await sendResetPasswordEmail({
        email,
        turnstileToken,
        turnstileTokenType: visibleCaptcha ? 'visible' : 'invisible',
      })

      if (response.status === 200) {
        setResetEmailSent(true)
      } else {
        setServerError('An error has occurreed. BR-238947')
      }
    } catch (e) {
      setServerError(e.message)
    }
    setLoading(false)
  }

  const { handleSubmit, register, setValue } = form

  return (
    <Dialog
      open={open}
      PaperProps={
        paperClassName
          ? {
              classes: {
                root: paperClassName,
              },
            }
          : {}
      }
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          right: 8,
          top: 8,
          alignSelf: 'end',
        }}
        className={styles.forgotPasswordModalCloseButton}
      >
        <img src={closeLogo} />
      </IconButton>
      <div className={styles.forgotPasswordModalContainer}>
        <FormBuilder
          form={form}
          className={styles.forgotPasswordModalForm}
          setFieldValue={setValue}
          recaptchaRef={recaptchaRef}
          formHeader={
            <div className={styles.forgotPasswordModalFormHeader}>
              <img
                className={styles.forgotPasswordModalLogo}
                src={brandLogo}
                alt="logo"
              />
              {!resetEmailSent && (
                <>
                  <span className={styles.forgotPasswordModalTitle}>
                    Forgot Your Password?
                  </span>
                  <span className={styles.forgotPasswordModalDescription}>
                    Enter the email address associated with this account and
                    we'll send you a link to reset your password.
                  </span>
                </>
              )}
              {resetEmailSent && (
                <>
                  <span className={styles.forgotPasswordModalTitle}>Sent!</span>
                  <span className={styles.forgotPasswordModalDescription}>
                    Thanks! We've sent you an email with a link to reset your
                    password.
                  </span>
                </>
              )}
            </div>
          }
          onSubmit={handleSubmit(onSubmit)}
          register={register}
          fields={
            !resetEmailSent
              ? [
                  {
                    name: 'email',
                    label: 'Email',
                    type: 'text',
                    required: true,
                  },
                ]
              : []
          }
        >
          <Turnstile
            ref={turnstileRef}
            options={{ refreshExpired: 'manual' }}
            style={{
              display: visibleCaptcha ? 'block' : 'none',
              alignSelf: 'center',
            }}
            siteKey={
              visibleCaptcha
                ? process.env.VISIBLE_TURNSTILE_SITE_KEY
                : process.env.INVISIBLE_TURNSTILE_SITE_KEY
            }
            onExpire={() => turnstileRef.current?.reset()}
          />
          {!resetEmailSent && (
            <Button
              disabled={loading}
              className={styles.forgotPasswordModalSubmitButton}
              variant="contained"
              type="submit"
            >
              {loading ? (
                <CircularProgress color="white" size={18} />
              ) : (
                'Send reset link'
              )}
            </Button>
          )}
        </FormBuilder>
        {serverError && (
          <span className={styles.forgotPasswordModalErrorMessage}>
            {serverError}
          </span>
        )}
      </div>
    </Dialog>
  )
}
